import { Inject, Injectable, NgZone } from '@angular/core'
import VectorSource from 'ol/source/Vector'
import { GeoJSON } from 'ol/format'
import { OpenLayersService } from './open-layers.service'
import { AuthContextStorageService } from '../../core/auth/auth-context-storage.service'
import { MultiPolygon, Polygon } from 'ol/geom'
import { OpenLayersProjectionService } from './open-layers-projection.service'
import { PostgisControllerService } from '../../api/backend'
import { EMPTY, Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { Feature } from 'ol'
import { FeatureLike } from 'ol/Feature'
import cloneDeep from 'lodash/cloneDeep'

@Injectable({
    providedIn: 'root'
})
export class OpenLayersGeoJsonService {
    // @ts-ignore
    private be_options = CA_CONFIG.BACKEND_OPTIONS

    constructor(
        @Inject(NgZone) private ngZone: NgZone,
        @Inject(OpenLayersService) private openLayersService: OpenLayersService,
        @Inject(OpenLayersProjectionService) private openLayersProjectionService: OpenLayersProjectionService,
        @Inject(PostgisControllerService) private postgisControllerService: PostgisControllerService,
        @Inject(AuthContextStorageService) private authContextStorageService: AuthContextStorageService,
    ) {
    }

    /**
     * All geoJsons should have property geoJson.properties.mapColorStyle.
     * @param geoJsons
     * @param vectorSource
     */
    public addGeoJsonFeaturesToVectorSource(geoJsons: any[], vectorSource: VectorSource): void {
        if (!geoJsons?.length) { return }

        const editedGeoJsons = cloneDeep(geoJsons)
        editedGeoJsons.forEach((geoJson) => {
            const feature: Feature[] = new GeoJSON().readFeatures(geoJson) as Feature[]
            feature[0].setStyle(this.openLayersService.getColorStyleFromIdentifier(geoJson.properties.mapColorStyle))
            vectorSource.addFeature(feature[0])
        })
    }

    public buildVectorSourceBboxGetFeatureWfsProjectLayerGeoserver(layerName: string): VectorSource<any> {
        return new VectorSource({
            format: new GeoJSON(),
            url: () => (
                this._getWfsProjectLayersUrl() + '?service=wfs&' +
                'version=1.1.0&request=GetFeature&typeName=' +
                this._getProjectLayerPrefix(this.authContextStorageService.getProjectKey()) +
                layerName +
                '&' +
                'outputFormat=application/json&' +
                'srsName=EPSG:5514&'
            ),
        })
    }

    public createVectorSourceWithUpdatedFeatureAreas(): VectorSource<any> {
        const vectorSource = new VectorSource()
        let mouseMoveTimeoutPromise = null

        vectorSource.on('addfeature', event => {
            if (event.feature) {
                this.getFeatureArea(event.feature).subscribe(area => {
                    this.ngZone.run(() => event.feature.set('area', area))
                })
            }
        })

        vectorSource.on('changefeature', event => {
            if (event.feature) {
                if (mouseMoveTimeoutPromise) {
                    clearTimeout(mouseMoveTimeoutPromise) // debounce if moved again
                }
                mouseMoveTimeoutPromise = setTimeout(() => {
                    this.getFeatureArea(event.feature).subscribe(area => {
                        this.ngZone.run(() => event.feature.set('area', area))
                    })
                }, 100) // 100ms wait is unperceivable but saves hundreds od http requests
            }
        })

        return vectorSource
    }

    public getFeatureArea(feature: Feature<any>): Observable<any> {
        if (feature.getGeometry() instanceof Polygon || feature.getGeometry() instanceof MultiPolygon) {
            return this.postgisControllerService.getAreaFromFeature(new GeoJSON().writeFeature(feature, {
                dataProjection: this.openLayersProjectionService.EPSG5514.code,
                featureProjection: this.openLayersProjectionService.EPSG5514.code
            })).pipe(map(area => Math.round(area)))
        }

        return EMPTY
    }

    public getFeaturesArea(features: Feature<any>[]): Observable<any> {
        return this.postgisControllerService.getAreaFromFeatures(new GeoJSON().writeFeatures(features, {
            dataProjection: this.openLayersProjectionService.EPSG5514.code,
            featureProjection: this.openLayersProjectionService.EPSG5514.code
        })).pipe(map(area => Math.round(area)))
    }

    public getFeaturesFromGeoJsons(geoJson: any): FeatureLike[] {
        return (new GeoJSON()).readFeatures(geoJson, {
            dataProjection: this.openLayersProjectionService.EPSG5514.code,
            featureProjection: this.openLayersProjectionService.EPSG5514.code
        })
    }

    public getGeoJsonFromFeature(feature: Feature<any>): string {
        return (new GeoJSON()).writeFeature(feature, {
            dataProjection: this.openLayersProjectionService.EPSG5514.code,
            featureProjection: this.openLayersProjectionService.EPSG5514.code
        })
    }

    public getGeoJsonFromFeatures(features: Feature<any>[]): string {
        return (new GeoJSON()).writeFeatures(features, {
            dataProjection: this.openLayersProjectionService.EPSG5514.code,
            featureProjection: this.openLayersProjectionService.EPSG5514.code
        })
    }

    public removeAllFeatures(vectorSource: VectorSource<any>): void {
        vectorSource?.getFeatures().forEach(feature => vectorSource.removeFeature(feature))
    }

    public removeFeaturesByIds(featureIds: any[], vectorSource: VectorSource<any>): void {
        featureIds.forEach((featureId) => {
            vectorSource.getFeatures().forEach(feature => {
                if (feature.getProperties().id === featureId) {
                    vectorSource.removeFeature(feature)
                }
            })
        })
    }

    ////////////////////////////////////////////////////////////////////////////////////////

    private _getWfsProjectLayersUrl(): string {
        return this.be_options.geoserverProjectUrl + '/ows'
    }

    private _getProjectLayerPrefix(projectKey): string {
        return 'assets:assets_' + projectKey + '_'
    }
}

